import type { Option } from "../core/option.ts";
import type { CellSnapshot } from "./mutation-backend.ts";
import type { ConfirmResult, PendingMutation } from "./pending-mutation.ts";
import type { MutationClientId } from "../ids/ids.ts";

/**
 * Applies one op to the snapshot in place — the domain-specific half of `derive`.
 * Events use `EventItem.applyUpdate`; tasks spread `{...item, ...patch}`; stickers
 * set raw cell data. The overlay mechanics around it are domain-agnostic.
 */
export type Reduce<Item, Op, Id extends string> = (snap: Map<Id, Item>, op: Op) => void;

/**
 * Local optimistic state as `base ⊕ overlay`: an authoritative `base` snapshot
 * plus an ordered log of pending mutations, generic over a domain's `Item` + `Op`
 * and the item's id `Id` (an `EventId`, a `TaskId`, a `CellId`), which keys the
 * snapshot. A pending entry is named by its `MutationClientId`, never by `Id`.
 * The visible state is `derive()` — `base` with the log applied in order through
 * the injected `reduce`.
 *
 * - `append(m)` adds a pending entry (the optimistic effect). Appends are the
 *   ISSUE ORDER: each entry gets the next issue sequence.
 * - `confirm(clientId, result)` folds the server result into base (upsert the
 *   canonical item, or delete an id) and removes the entry (ack).
 * - `drop(clientId)` removes the entry without folding — automatic rollback on
 *   error; base is unchanged.
 * - `expire(clientId)` is a `drop` by the settle reaper: the entry leaves the
 *   log, but its issue sequence is kept so a late `ack` still folds in order,
 *   and the items it writes stay held until it is answered (invariant (v)).
 * - `ack(clientId, op)` settles an entry whose server answer carries no
 *   canonical item (a field-level patch that landed): the entry's OWN op is
 *   folded into base, in issue order — a landed entry behind an older one still
 *   in flight waits, still applied in `derive`, until everything before it
 *   settles. So base only ever holds landed ops applied in the order they were
 *   issued, and a later refusal of an older entry takes back only that entry's
 *   op — never a newer landed one, and never re-installing a stale whole item.
 *
 * Invariant (i) — landed patches fold in issue order, EVEN ACROSS EXPIRY. With
 * a `Fields` description of the domain's ops the overlay records, per item and
 * field, the issue sequence of the newest op folded into base. Every fold
 * writes only the fields no newer op already folded, so a late ack of an
 * expired entry cannot overwrite a field a newer landed op wrote, while its
 * other fields (and an isolated slow success) still land. The rule lives in
 * the one `fold` every path goes through — in-log fold, late ack, confirm —
 * so no settle order can bypass it. Without `Fields` (a domain that never
 * acks) a late ack folds its whole op.
 *
 * Invariant (v) — an unanswered entry's fields never reach base, EVEN ACROSS
 * EXPIRY. base is the confirmed state: the value it was seeded with plus the
 * landed ops folded in issue order; the view is base plus the entries still
 * in the log. An item that an UNSETTLED entry writes — one still in the log,
 * or one `expire` took out that has not been answered yet — is HELD: its base
 * value changes only through `fold` / `confirm`. `rebase` ignores a held item
 * and `setBase` keeps its current value, so a caller's item (which may carry
 * an upper layer's optimistic projection of that very entry) can never
 * become its base while the entry's answer is outstanding. An empty log is
 * therefore not taken as proof that a caller's item holds only confirmed
 * fields. The held set comes from `Fields`; without it (a domain that never
 * expires by field) `rebase` trusts its caller and `setBase` replaces all.
 *
 * Rebuilds re-derive deterministically, so there is no "index vs store"
 * disagreement — one derivation is the truth (the bug class Moves 1–4 delete).
 * Later log entries win (per-cell last-writer-wins). The backend supplies base and
 * the `ConfirmResult`; the overlay never inspects `Op` (a domain concern — the
 * injected `Fields` answers what an op writes) nor reads an id off `Item` (the
 * `ConfirmResult` carries the base key), so it is agnostic to the op shape.
 */
export class Overlay<Item, Op, Id extends string> {
  #base: Map<Id, Item>;
  #log: PendingMutation<Op>[] = [];
  /** ClientIds in `log` whose op landed (`ack`) and waits for the entries
   *  before it to settle before folding into base. */
  readonly #landed = new Set<MutationClientId>();
  /** Issue sequence of every entry in `log`. */
  readonly #issued = new Map<MutationClientId, number>();
  /** Every entry `expire` took out of the log, with its issue sequence,
   *  until its late settle (`ack` / `confirm` / `drop`) consumes it. Its op
   *  keeps the items it writes held (invariant (v)). */
  readonly #expired = new Map<
    MutationClientId,
    { readonly seq: number; readonly op: Op }
  >();
  /** Per item id, per field: the issue sequence of the newest op folded into
   *  base that wrote it (`Overlay.WHOLE`: the whole item). */
  readonly #foldedAt = new Map<Id, Map<string, number>>();
  #nextSeq = 0;

  /**
   * @param fields  What each op writes, field by field — the ordering half of
   *   invariant (i). `null` for a domain whose ops never settle by `ack`.
   */
  readonly #reduce: Reduce<Item, Op, Id>;
  readonly #fields: Option<Overlay.Fields<Op, Id>>;
  constructor(
    base: CellSnapshot<Item, Id>,
    reduce: Reduce<Item, Op, Id>,
    fields: Option<Overlay.Fields<Op, Id>>,
  ) {
    this.#fields = fields;
    this.#reduce = reduce;
    this.#base = new Map(base);
  }

  /** Replace the authoritative base (e.g. after a fresh `snapshot()`), keeping
   *  the pending log so in-flight optimism survives a base refresh. A held
   *  item keeps its current base value (invariant (v)). */
  setBase(base: CellSnapshot<Item, Id>): void {
    const next = new Map(base);
    for (const id of this.#held()) {
      const kept = this.#base.get(id);
      if (kept === undefined) next.delete(id);
      else next.set(id, kept);
    }
    this.#base = next;
  }

  append(m: PendingMutation<Op>): void {
    this.#log.push(m);
    this.#issued.set(m.clientId, this.#nextSeq++);
  }

  /** Fold a confirmed mutation into base and remove it from the log. The
   *  optimistic entry — which may have rendered under an optimistic id — leaves
   *  the overlay, and the canonical value lands in base under its real id. The
   *  canonical is the server's whole item, so it is folded unconditionally and
   *  recorded as a whole-item write at the entry's issue sequence. */
  confirm(clientId: MutationClientId, result: ConfirmResult<Item, Id>): void {
    const seq = this.#seqOf(clientId);
    this.#remove(clientId);
    this.#expired.delete(clientId);
    if (result.kind === "upsert") this.#base.set(result.id, result.item);
    else this.#base.delete(result.id);
    if (seq != null) this.#record(result.id, Overlay.WHOLE, seq);
    this.#foldLanded();
  }

  /**
   * Settle a pending mutation that landed without a canonical item: its own
   * `op` folds into base in issue order (see the class doc). While an older
   * entry is still in flight the landed entry stays in the log — `derive`
   * keeps applying it — and folds once every entry before it settled.
   *
   * `op` is the entry's own op, used when the entry is no longer in the log
   * (a late answer after the settle reaper expired it). It folds at the issue
   * sequence `expire` kept, so only the fields no newer landed op wrote reach
   * base: a slow-but-successful write still converges, and never over a newer
   * one. An entry the overlay never saw folds as the oldest write.
   */
  ack(clientId: MutationClientId, op: Op): void {
    if (this.#log.some((m) => m.clientId === clientId)) {
      this.#landed.add(clientId);
      this.#foldLanded();
      return;
    }
    const seq = this.#expired.get(clientId)?.seq ?? -1;
    this.#expired.delete(clientId);
    this.#fold(op, seq);
  }

  /** Drop a pending mutation without folding — automatic rollback on error. */
  drop(clientId: MutationClientId): void {
    this.#remove(clientId);
    this.#expired.delete(clientId);
    this.#foldLanded();
  }

  /** The settle reaper's `drop`: roll the entry back out of the view, but keep
   *  its issue sequence for a late `ack` (invariant (i)) and its op, so the
   *  items it writes stay held until it is answered (invariant (v)). */
  expire(clientId: MutationClientId): void {
    const seq = this.#issued.get(clientId);
    const entry = this.#log.find((m) => m.clientId === clientId);
    this.#remove(clientId);
    if (seq != null && entry != null) {
      this.#expired.set(clientId, { seq, op: entry.op });
    }
    this.#foldLanded();
  }

  /**
   * Replace base's value for `id` with `item` — the item a caller applies a
   * new op against, when no unsettled entry writes `id`. Returns whether it
   * was taken: a held item (invariant (v)) keeps its base, since `item` may
   * carry an unanswered entry's fields. Without `Fields` the overlay cannot
   * tell which items an op writes, so it trusts the caller.
   */
  rebase(id: Id, item: Item): boolean {
    if (this.#held().has(id)) return false;
    this.#base.set(id, item);
    return true;
  }

  /** The ops `expire` took out that have not been answered yet, in issue
   *  order — their items are held (invariant (v)). */
  expiredOps(): readonly Op[] {
    return [...this.#expired.values()]
      .sort((a, b) => a.seq - b.seq)
      .map((entry) => entry.op);
  }

  /** Every item an unsettled entry writes: the log's and the unanswered
   *  expired ones' (invariant (v)). Empty without `Fields`. */
  #held(): Set<Id> {
    const held = new Set<Id>();
    const fields = this.#fields;
    if (fields == null) return held;
    const ops = [...this.#log.map((m) => m.op), ...this.expiredOps()];
    for (const op of ops) {
      for (const slot of fields.slots(op)) held.add(slot.id);
    }
    return held;
  }

  #seqOf(clientId: MutationClientId): Option<number> {
    return this.#issued.get(clientId) ?? this.#expired.get(clientId)?.seq ??
      null;
  }

  #remove(clientId: MutationClientId): void {
    this.#log = this.#log.filter((m) => m.clientId !== clientId);
    this.#landed.delete(clientId);
    this.#issued.delete(clientId);
  }

  /** Fold every landed entry at the head of the log into base, in order. */
  #foldLanded(): void {
    for (let head = this.#log[0]; head !== undefined && this.#landed.has(head.clientId); head = this.#log[0]) {
      const seq = this.#issued.get(head.clientId) ?? -1;
      this.#remove(head.clientId);
      this.#fold(head.op, seq);
    }
  }

  /**
   * Fold `op`, issued at `seq`, into base — the ONE place a landed patch
   * reaches base. Writes only the fields no newer folded op wrote, and
   * records them at `seq` (invariant (i)).
   */
  #fold(op: Op, seq: number): void {
    const fields = this.#fields;
    if (fields == null) {
      this.#reduce(this.#base, op);
      return;
    }
    const slots = fields.slots(op);
    const keep = slots.filter((slot) => this.#newestFold(slot) < seq);
    if (keep.length === 0) return;
    const kept = keep.length === slots.length ? op : fields.restrict(op, keep);
    if (kept == null) return;
    this.#reduce(this.#base, kept);
    for (const slot of keep) this.#record(slot.id, slot.field, seq);
  }

  /** Issue sequence of the newest folded op that wrote `slot` (-1: none). A
   *  whole-item write covers every field; a whole-item slot is covered by any
   *  field write. */
  #newestFold(slot: Overlay.Slot<Id>): number {
    const byField = this.#foldedAt.get(slot.id);
    if (byField == null) return -1;
    if (slot.field === Overlay.WHOLE) return Math.max(-1, ...byField.values());
    return Math.max(
      byField.get(slot.field) ?? -1,
      byField.get(Overlay.WHOLE) ?? -1,
    );
  }

  #record(id: Id, field: string, seq: number): void {
    let byField = this.#foldedAt.get(id);
    if (byField == null) this.#foldedAt.set(id, byField = new Map());
    byField.set(field, Math.max(byField.get(field) ?? -1, seq));
  }

  pending(): readonly PendingMutation<Op>[] {
    return this.#log;
  }

  /** The visible state: `base` with the pending log applied in order. */
  derive(): CellSnapshot<Item, Id> {
    const out = new Map(this.#base);
    for (const m of this.#log) this.#reduce(out, m.op);
    return out;
  }
}

/** Types and values that go with `Overlay`: `WHOLE`, `Slot`, `Fields`. */
export namespace Overlay {
  /** The field name of a write that replaces (or removes) the whole item. */
  export const WHOLE = "*";

  /** One field of one item (by the item's id): what an op writes. */
  export type Slot<Id extends string> = { readonly id: Id; readonly field: string };

  /**
   * The domain's answer to "what does this op write" — the only op knowledge
   * the overlay needs to fold landed patches in issue order per field.
   */
  export interface Fields<Op, Id extends string> {
    /** Every `(item id, field)` `op` writes; `WHOLE` for an insert/delete. */
    slots(op: Op): readonly Slot<Id>[];
    /** `op` narrowed to write only `keep` (a subset of `slots(op)`), or null
     *  when it cannot be narrowed — the fold then skips it. */
    restrict(op: Op, keep: readonly Slot<Id>[]): Option<Op>;
  }
}
